import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  DocumentIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  ViewColumnsIcon,
  Squares2X2Icon,
  ChevronDownIcon,
  CalendarIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  HeartIcon,
  BookmarkIcon,
  ShareIcon,
  TrashIcon,
  PencilIcon,
  FolderIcon,
  ClockIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid, BookmarkIcon as BookmarkIconSolid, StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const MyDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [activeTab, setActiveTab] = useState('uploaded');

  useEffect(() => {
    // Simulate fetching user's documents
    const fetchDocuments = () => {
      const mockDocuments = [
        {
          id: 1,
          title: 'Patient Care Protocol Update',
          fileName: 'patient-care-protocol-v2.pdf',
          type: 'PDF',
          category: 'Medical Procedures',
          size: '1.8 MB',
          uploadDate: '2025-01-20',
          lastModified: '2025-01-22',
          author: 'Current User',
          description: 'Updated patient care protocols for the cardiology department.',
          downloads: 45,
          views: 89,
          tags: ['Protocol', 'Cardiology', 'Patient Care'],
          isOwner: true,
          isLiked: false,
          isBookmarked: true,
          isFavorite: true,
          permissions: {
            canEdit: true,
            canDelete: true,
            canShare: true
          }
        },
        {
          id: 2,
          title: 'Emergency Response Checklist',
          fileName: 'emergency-response-checklist.doc',
          type: 'DOC',
          category: 'Safety Protocols',
          size: '0.8 MB',
          uploadDate: '2025-01-18',
          lastModified: '2025-01-19',
          author: 'Current User',
          description: 'Quick reference checklist for emergency response procedures.',
          downloads: 78,
          views: 156,
          tags: ['Emergency', 'Checklist', 'Safety'],
          isOwner: true,
          isLiked: true,
          isBookmarked: false,
          isFavorite: false,
          permissions: {
            canEdit: true,
            canDelete: true,
            canShare: true
          }
        },
        {
          id: 3,
          title: 'Training Session Notes',
          fileName: 'training-session-notes-jan2025.docx',
          type: 'DOCX',
          category: 'Training Materials',
          size: '1.2 MB',
          uploadDate: '2025-01-15',
          lastModified: '2025-01-16',
          author: 'Current User',
          description: 'Notes from the January 2025 training session on new equipment.',
          downloads: 23,
          views: 45,
          tags: ['Training', 'Notes', 'Equipment'],
          isOwner: true,
          isLiked: true,
          isBookmarked: true,
          isFavorite: true,
          permissions: {
            canEdit: true,
            canDelete: true,
            canShare: true
          }
        },
        {
          id: 4,
          title: 'Employee Handbook 2025',
          fileName: 'employee-handbook-2025.pdf',
          type: 'PDF',
          category: 'HR Policies',
          size: '2.4 MB',
          uploadDate: '2025-01-15',
          lastModified: '2025-01-20',
          author: 'Dr. Sarah Johnson',
          description: 'Comprehensive employee handbook covering policies and procedures.',
          downloads: 145,
          views: 289,
          tags: ['HR', 'Policies', 'Guidelines'],
          isOwner: false,
          isLiked: true,
          isBookmarked: true,
          isFavorite: false,
          permissions: {
            canEdit: false,
            canDelete: false,
            canShare: true
          }
        },
        {
          id: 5,
          title: 'Research Paper Draft',
          fileName: 'cardiac-research-draft.pdf',
          type: 'PDF',
          category: 'Research',
          size: '3.2 MB',
          uploadDate: '2025-01-10',
          lastModified: '2025-01-14',
          author: 'Current User',
          description: 'Draft research paper on cardiac intervention outcomes.',
          downloads: 8,
          views: 24,
          tags: ['Research', 'Cardiology', 'Draft'],
          isOwner: true,
          isLiked: false,
          isBookmarked: false,
          isFavorite: true,
          permissions: {
            canEdit: true,
            canDelete: true,
            canShare: false
          }
        },
        {
          id: 6,
          title: 'IT Security Policy',
          fileName: 'it-security-policy.doc',
          type: 'DOC',
          category: 'IT Security',
          size: '1.2 MB',
          uploadDate: '2025-01-03',
          lastModified: '2025-01-05',
          author: 'David Kim',
          description: 'Information technology security policies and procedures.',
          downloads: 67,
          views: 134,
          tags: ['IT', 'Security', 'Policy'],
          isOwner: false,
          isLiked: false,
          isBookmarked: true,
          isFavorite: false,
          permissions: {
            canEdit: false,
            canDelete: false,
            canShare: true
          }
        }
      ];

      setDocuments(mockDocuments);
      setLoading(false);
    };

    setTimeout(fetchDocuments, 500);
  }, []);

  const filterDocumentsByTab = (docs) => {
    switch (activeTab) {
      case 'uploaded':
        return docs.filter(doc => doc.isOwner);
      case 'liked':
        return docs.filter(doc => doc.isLiked);
      case 'bookmarked':
        return docs.filter(doc => doc.isBookmarked);
      case 'favorites':
        return docs.filter(doc => doc.isFavorite);
      default:
        return docs;
    }
  };

  const filteredDocuments = filterDocumentsByTab(documents).filter(document => {
    const matchesSearch = document.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         document.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         document.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = !selectedType || document.type === selectedType;
    const matchesCategory = !selectedCategory || document.category === selectedCategory;
    
    const matchesDate = !dateFilter || (() => {
      const docDate = new Date(document.lastModified);
      const now = new Date();
      const daysDiff = Math.floor((now - docDate) / (1000 * 60 * 60 * 24));
      
      switch (dateFilter) {
        case 'today':
          return daysDiff === 0;
        case 'week':
          return daysDiff <= 7;
        case 'month':
          return daysDiff <= 30;
        case 'year':
          return daysDiff <= 365;
        default:
          return true;
      }
    })();

    return matchesSearch && matchesType && matchesCategory && matchesDate;
  });

  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'category':
        comparison = a.category.localeCompare(b.category);
        break;
      case 'size':
        comparison = parseFloat(a.size) - parseFloat(b.size);
        break;
      case 'downloads':
        comparison = a.downloads - b.downloads;
        break;
      case 'views':
        comparison = a.views - b.views;
        break;
      default: // date
        comparison = new Date(a.lastModified) - new Date(b.lastModified);
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const uniqueTypes = [...new Set(documents.map(doc => doc.type))];
  const uniqueCategories = [...new Set(documents.map(doc => doc.category))];

  const handleToggleLike = (docId) => {
    setDocuments(docs => docs.map(doc => 
      doc.id === docId ? { ...doc, isLiked: !doc.isLiked } : doc
    ));
  };

  const handleToggleBookmark = (docId) => {
    setDocuments(docs => docs.map(doc => 
      doc.id === docId ? { ...doc, isBookmarked: !doc.isBookmarked } : doc
    ));
  };

  const handleToggleFavorite = (docId) => {
    setDocuments(docs => docs.map(doc => 
      doc.id === docId ? { ...doc, isFavorite: !doc.isFavorite } : doc
    ));
  };

  const handleDelete = (docId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      setDocuments(docs => docs.filter(doc => doc.id !== docId));
    }
  };

  const tabs = [
    { id: 'uploaded', name: 'My Uploads', count: documents.filter(doc => doc.isOwner).length },
    { id: 'liked', name: 'Liked', count: documents.filter(doc => doc.isLiked).length },
    { id: 'bookmarked', name: 'Bookmarked', count: documents.filter(doc => doc.isBookmarked).length },
    { id: 'favorites', name: 'Favorites', count: documents.filter(doc => doc.isFavorite).length }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Custom Header for My Documents */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">My Documents</h1>
                <p className="mt-2 text-sm text-gray-700">
                  Manage your uploaded documents, bookmarks, and favorites
                </p>
              </div>
              <div className="mt-4 sm:mt-0">
                <Link
                  to="/documents/upload"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <DocumentIcon className="-ml-1 mr-2 h-4 w-4" />
                  Upload Document
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  {tab.name}
                  <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                    {tab.count}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Search and Filters */}
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              {/* Search */}
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Search my documents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center space-x-4">
                {/* Filters Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                  Filters
                </button>

                {/* Sort */}
                <div className="relative">
                  <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field);
                      setSortOrder(order);
                    }}
                    className="block appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm leading-5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="date-desc">Newest First</option>
                    <option value="date-asc">Oldest First</option>
                    <option value="title-asc">Title A-Z</option>
                    <option value="title-desc">Title Z-A</option>
                    <option value="category-asc">Category A-Z</option>
                    <option value="views-desc">Most Viewed</option>
                    <option value="downloads-desc">Most Downloaded</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <ChevronDownIcon className="h-4 w-4" />
                  </div>
                </div>

                {/* View Toggle */}
                <div className="flex rounded-md shadow-sm">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`${
                      viewMode === 'grid'
                        ? 'bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:text-gray-700'
                    } relative inline-flex items-center px-3 py-2 rounded-l-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                  >
                    <Squares2X2Icon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`${
                      viewMode === 'list'
                        ? 'bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:text-gray-700'
                    } relative inline-flex items-center px-3 py-2 -ml-px rounded-r-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                  >
                    <ViewColumnsIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      File Type
                    </label>
                    <select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Types</option>
                      {uniqueTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Categories</option>
                      {uniqueCategories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date Modified
                    </label>
                    <select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                      <option value="year">This Year</option>
                    </select>
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        setSelectedType('');
                        setSelectedCategory('');
                        setDateFilter('');
                        setSearchTerm('');
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Documents Grid/List */}
        {sortedDocuments.length === 0 ? (
          <div className="text-center py-12">
            <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No documents found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {activeTab === 'uploaded' ? "You haven't uploaded any documents yet." : 
               activeTab === 'liked' ? "You haven't liked any documents yet." :
               activeTab === 'bookmarked' ? "You haven't bookmarked any documents yet." :
               "You haven't added any documents to favorites yet."}
            </p>
            {activeTab === 'uploaded' && (
              <div className="mt-6">
                <Link
                  to="/documents/upload"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Upload Your First Document
                </Link>
              </div>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedDocuments.map((document) => (
              <div key={document.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center flex-1">
                      <DocumentIcon className="h-8 w-8 text-blue-500" />
                      <div className="ml-3 flex-1 min-w-0">
                        <Link
                          to={`/documents/${document.id}`}
                          className="text-lg font-medium text-gray-900 hover:text-blue-600 block truncate"
                        >
                          {document.title}
                        </Link>
                        <p className="text-sm text-gray-500 truncate">{document.fileName}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      {document.isFavorite && (
                        <StarIconSolid className="h-4 w-4 text-yellow-400" />
                      )}
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {document.type}
                      </span>
                    </div>
                  </div>
                  
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                    {document.description}
                  </p>
                  
                  <div className="mt-4 flex items-center text-sm text-gray-500">
                    <FolderIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                    {document.category}
                  </div>
                  
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                    {document.lastModified}
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <EyeIcon className="h-4 w-4 mr-1" />
                        {document.views}
                      </div>
                      <div className="flex items-center">
                        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                        {document.downloads}
                      </div>
                    </div>
                    <span className="text-xs">{document.size}</span>
                  </div>
                  
                  {/* Actions */}
                  <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleLike(document.id)}
                        className="p-1 rounded-full hover:bg-gray-100"
                        title={document.isLiked ? 'Unlike' : 'Like'}
                      >
                        {document.isLiked ? (
                          <HeartIconSolid className="h-4 w-4 text-red-500" />
                        ) : (
                          <HeartIcon className="h-4 w-4 text-gray-400 hover:text-red-500" />
                        )}
                      </button>
                      <button
                        onClick={() => handleToggleBookmark(document.id)}
                        className="p-1 rounded-full hover:bg-gray-100"
                        title={document.isBookmarked ? 'Remove bookmark' : 'Bookmark'}
                      >
                        {document.isBookmarked ? (
                          <BookmarkIconSolid className="h-4 w-4 text-yellow-500" />
                        ) : (
                          <BookmarkIcon className="h-4 w-4 text-gray-400 hover:text-yellow-500" />
                        )}
                      </button>
                      <button
                        onClick={() => handleToggleFavorite(document.id)}
                        className="p-1 rounded-full hover:bg-gray-100"
                        title={document.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        {document.isFavorite ? (
                          <StarIconSolid className="h-4 w-4 text-yellow-400" />
                        ) : (
                          <StarIcon className="h-4 w-4 text-gray-400 hover:text-yellow-400" />
                        )}
                      </button>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {document.permissions.canShare && (
                        <button className="p-1 rounded-full hover:bg-gray-100" title="Share">
                          <ShareIcon className="h-4 w-4 text-gray-400 hover:text-blue-500" />
                        </button>
                      )}
                      {document.permissions.canEdit && (
                        <button className="p-1 rounded-full hover:bg-gray-100" title="Edit">
                          <PencilIcon className="h-4 w-4 text-gray-400 hover:text-blue-500" />
                        </button>
                      )}
                      {document.permissions.canDelete && (
                        <button 
                          onClick={() => handleDelete(document.id)}
                          className="p-1 rounded-full hover:bg-gray-100" 
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4 text-gray-400 hover:text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {document.tags && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {document.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="divide-y divide-gray-200">
              {sortedDocuments.map((document) => (
                <div key={document.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1 min-w-0">
                      <DocumentIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
                      <div className="ml-4 flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Link
                              to={`/documents/${document.id}`}
                              className="text-lg font-medium text-gray-900 hover:text-blue-600 truncate"
                            >
                              {document.title}
                            </Link>
                            {document.isFavorite && (
                              <StarIconSolid className="h-4 w-4 text-yellow-400" />
                            )}
                          </div>
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {document.type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{document.description}</p>
                        <div className="mt-2 flex items-center space-x-6 text-sm text-gray-500">
                          <div className="flex items-center">
                            <FolderIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                            {document.category}
                          </div>
                          <div className="flex items-center">
                            <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                            {document.lastModified}
                          </div>
                          <div className="flex items-center">
                            <EyeIcon className="h-4 w-4 mr-1" />
                            {document.views}
                          </div>
                          <div className="flex items-center">
                            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                            {document.downloads}
                          </div>
                          <span>{document.size}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleToggleLike(document.id)}
                        className="p-2 rounded-full hover:bg-gray-100"
                      >
                        {document.isLiked ? (
                          <HeartIconSolid className="h-4 w-4 text-red-500" />
                        ) : (
                          <HeartIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                      <button
                        onClick={() => handleToggleBookmark(document.id)}
                        className="p-2 rounded-full hover:bg-gray-100"
                      >
                        {document.isBookmarked ? (
                          <BookmarkIconSolid className="h-4 w-4 text-yellow-500" />
                        ) : (
                          <BookmarkIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                      <button
                        onClick={() => handleToggleFavorite(document.id)}
                        className="p-2 rounded-full hover:bg-gray-100"
                      >
                        {document.isFavorite ? (
                          <StarIconSolid className="h-4 w-4 text-yellow-400" />
                        ) : (
                          <StarIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                      {document.permissions.canShare && (
                        <button className="p-2 rounded-full hover:bg-gray-100">
                          <ShareIcon className="h-4 w-4 text-gray-400" />
                        </button>
                      )}
                      {document.permissions.canEdit && (
                        <button className="p-2 rounded-full hover:bg-gray-100">
                          <PencilIcon className="h-4 w-4 text-gray-400" />
                        </button>
                      )}
                      {document.permissions.canDelete && (
                        <button 
                          onClick={() => handleDelete(document.id)}
                          className="p-2 rounded-full hover:bg-gray-100"
                        >
                          <TrashIcon className="h-4 w-4 text-gray-400" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default MyDocuments;
